import { useEffect, useRef, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Apple, Hammer, Loader2, Save, CheckCircle2, XCircle, Clock, KeyRound, UploadCloud, Images } from "lucide-react";

export default function IosAppBuilder() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);
  const [form, setForm] = useState({
    app_name: "", bundle_id: "", team_id: "", issuer_id: "", key_id: "", api_key_p8: "",
  });
  const [splashB64, setSplashB64] = useState(null);
  const [splashPreview, setSplashPreview] = useState(null);
  const splashRef = useRef();

  const load = async () => {
    try {
      const { data: d } = await api.get("/merchant/app-builder/ios");
      setData(d);
      const c = d.config || {};
      setForm((f) => ({
        ...f,
        app_name: c.app_name || "",
        bundle_id: c.bundle_id || "",
        team_id: c.team_id || "",
        issuer_id: c.issuer_id || "",
        key_id: c.key_id || "",
        api_key_p8: "",
      }));
      if (c.splash_b64) setSplashPreview(c.splash_b64);
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.api_key_p8) delete body.api_key_p8;
      if (splashB64) body.splash_b64 = splashB64;
      await api.put("/merchant/app-builder/ios/config", body);
      setSplashB64(null);
      toast.success("iOS settings saved");
      await load();
    } catch (e) {
      toast.error(extractErr(e));
    } finally { setSaving(false); }
  };

  const build = async () => {
    setBuilding(true);
    try {
      await api.post("/merchant/app-builder/ios/build");
      toast.success("iOS build started on Codemagic");
      await load();
    } catch (e) {
      toast.error(extractErr(e));
    } finally { setBuilding(false); }
  };

  if (!data) return <div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading iOS builder…</div>;

  const cm = data.codemagic || {};
  const builds = data.builds || [];

  return (
    <div className="space-y-6" data-testid="ios-builder">
      <Card className="p-5 border-slate-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Apple className="w-5 h-5" /></div>
          <div>
            <div className="font-display font-bold text-lg">Your Apple Developer account</div>
            <p className="text-sm text-slate-500 mt-1">Each store publishes under the merchant’s own Apple account. You pay Apple’s $99/year Developer Program separately. We collect your App Store Connect API key so Codemagic can sign the IPA.</p>
          </div>
        </div>
      </Card>

      {!cm.configured ? (
        <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-950" data-testid="ios-codemagic-pending">
          Codemagic is not connected on the platform yet. You can save your Apple credentials now. Builds will start as soon as Super Admin adds the Codemagic API token.
        </Card>
      ) : (
        <Card className="p-4 border-emerald-200 bg-emerald-50 text-sm text-emerald-900" data-testid="ios-codemagic-ready">
          Codemagic is connected. Builds run on Apple-certified Macs in the cloud.
        </Card>
      )}

      <Card className="p-6 space-y-4">
        <div className="font-semibold text-slate-900 flex items-center gap-2"><KeyRound className="w-4 h-4" /> App Store Connect</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>iOS app name</Label>
            <Input className="mt-1" data-testid="ios-app-name" value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder="My Store" />
          </div>
          <div>
            <Label>Bundle ID</Label>
            <Input className="mt-1 font-mono-nums" data-testid="ios-bundle-id" value={form.bundle_id} onChange={(e) => setForm({ ...form, bundle_id: e.target.value })} placeholder="com.yourcompany.yourapp" />
          </div>
          <div>
            <Label>Apple Team ID</Label>
            <Input className="mt-1 font-mono-nums" data-testid="ios-team-id" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} placeholder="ABCDE12345" />
          </div>
          <div>
            <Label>Issuer ID</Label>
            <Input className="mt-1 font-mono-nums" data-testid="ios-issuer-id" value={form.issuer_id} onChange={(e) => setForm({ ...form, issuer_id: e.target.value })} />
          </div>
          <div>
            <Label>Key ID</Label>
            <Input className="mt-1 font-mono-nums" data-testid="ios-key-id" value={form.key_id} onChange={(e) => setForm({ ...form, key_id: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Splash screen background</Label>
          <div className="flex items-center gap-3 mt-1">
            <div className="w-16 h-24 rounded-xl border overflow-hidden bg-slate-50 flex items-center justify-center">
              {splashPreview ? <img src={splashPreview} alt="splash" className="w-full h-full object-cover" />
                : <Images className="w-5 h-5 text-slate-400" />}
            </div>
            <input ref={splashRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="ios-splash-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 3 * 1024 * 1024) { toast.error("Splash image must be under 3MB"); return; }
                const reader = new FileReader();
                reader.onload = () => { setSplashB64(reader.result); setSplashPreview(reader.result); };
                reader.readAsDataURL(file);
              }} />
            <Button type="button" variant="outline" size="sm" onClick={() => splashRef.current?.click()}>
              <UploadCloud className="w-4 h-4 mr-1" /> Upload splash
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-1">Same splash used for Android and iOS. Save, then generate the app.</p>
        </div>
        {data.ondemand_theme ? (
          <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3" data-testid="ios-delivery-row">
            <div>
              <div className="text-sm font-medium text-slate-800">Delivery app</div>
              <div className="text-xs text-slate-500">Required for the Ondemand theme and baked into every iOS build while that theme is active.</div>
            </div>
            <Switch checked disabled data-testid="builder-delivery-app" />
          </div>
        ) : null}
        <div>
          <Label>AuthKey .p8 private key {data.config?.has_api_key ? <span className="text-emerald-600 font-normal">· stored</span> : null}</Label>
          <Textarea className="mt-1 font-mono text-xs" rows={6} data-testid="ios-p8" value={form.api_key_p8} onChange={(e) => setForm({ ...form, api_key_p8: e.target.value })} placeholder={data.config?.has_api_key ? "Leave blank to keep the stored key" : "-----BEGIN PRIVATE KEY-----"} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} data-testid="ios-save"><Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save iOS settings"}</Button>
          <Button onClick={build} disabled={building} className="bg-slate-900 hover:bg-slate-800" data-testid="ios-build">
            {building ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Hammer className="w-4 h-4 mr-1" />}
            Build iOS app
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <div className="font-semibold mb-3">iOS builds</div>
        {builds.length === 0 ? <p className="text-sm text-slate-400">No iOS builds yet.</p> : (
          <div className="space-y-3">
            {builds.map((b) => (
              <div key={b.id} className="border rounded-lg p-3" data-testid="ios-build-row">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {b.status === "success" ? <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Ready</Badge>
                      : b.status === "failed" ? <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>
                        : <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />{b.status}</Badge>}
                    <span className="text-xs text-slate-500">{b.app_name} · {b.bundle_id}</span>
                  </div>
                  <span className="text-xs text-slate-400">{b.created_at ? new Date(b.created_at).toLocaleString() : ""}</span>
                </div>
                {b.error ? <div className="mt-2 text-xs text-red-600">{b.error}</div> : null}
                {b.codemagic_build_id ? <div className="mt-1 text-[11px] text-slate-400 font-mono">Codemagic {b.codemagic_build_id}</div> : null}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
